import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, Building2, Search, Mail, Phone, MapPin, Trophy, Cake,
  Clock, Grid3X3, List, ChevronRight, ChevronDown, CalendarDays
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format, differenceInYears, differenceInDays, isSameMonth, isAfter, isBefore, startOfDay, addDays } from "date-fns";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { useRole } from "@/hooks/useRole";
import { cn } from "@/lib/utils";

function OrgTreeView({ employees, onEmployeeClick }: { employees: any[], onEmployeeClick: (emp: any) => void }) {
  const [expandedManagerIds, setExpandedManagerIds] = useState<Set<number>>(new Set());
  const hasInitializedExpanded = useRef(false);

  const topLevelManagers = useMemo(() => {
    // Find employees who have reports but don't have a manager themselves
    const managerIds = new Set(employees.filter(e => e.managerEmployeeId).map(e => e.managerEmployeeId));
    return employees.filter(e => managerIds.has(e.id) && !e.managerEmployeeId);
  }, [employees]);

  useEffect(() => {
    if (hasInitializedExpanded.current) return;
    if (topLevelManagers.length === 0) return;
    setExpandedManagerIds(new Set(topLevelManagers.map(manager => manager.id)));
    hasInitializedExpanded.current = true;
  }, [topLevelManagers]);

  const getDirectReports = (managerId: number) => {
    return employees.filter(e => e.managerEmployeeId === managerId);
  };

  // Card component for displaying an employee
  const OrgCard = ({ employee, isRoot = false }: { employee: any; isRoot?: boolean }) => {
    const reports = getDirectReports(employee.id);
    const hasReports = reports.length > 0;
    const isExpanded = expandedManagerIds.has(employee.id);

    const handleCardClick = () => {
      if (hasReports) {
        setExpandedManagerIds(prev => {
          const next = new Set(prev);
          if (next.has(employee.id)) {
            next.delete(employee.id);
          } else {
            next.add(employee.id);
          }
          return next;
        });
        return;
      }

      onEmployeeClick(employee);
    };

    return (
      <div
        className={cn(
          "bg-white dark:bg-gray-900 border-2 rounded-lg p-4 min-w-[180px] max-w-[220px] cursor-pointer hover:shadow-lg transition-shadow",
          isRoot ? "border-slate-700 dark:border-slate-400" : "border-slate-300 dark:border-slate-600"
        )}
        onClick={handleCardClick}
        onDoubleClick={() => onEmployeeClick(employee)}
      >
        <div className="flex items-start gap-3">
          <Avatar className={cn("h-12 w-12 flex-shrink-0", isRoot ? "ring-2 ring-slate-700 dark:ring-slate-400" : "")}>
            {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
            <AvatarFallback className={cn(
              "text-base font-semibold",
              isRoot
                ? "bg-slate-700 text-white dark:bg-slate-300 dark:text-slate-900"
                : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
            )}>
              {employee.firstName?.[0]}{employee.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
              {employee.firstName} {employee.lastName}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 truncate italic">
              {employee.position || 'No title'}
            </p>
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wide">Department</p>
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                {employee.department || 'Unassigned'}
              </p>
            </div>
          </div>
        </div>
        {hasReports && (
          <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-1">
            {isExpanded ? (
              <ChevronDown className="h-3 w-3 text-slate-500" />
            ) : (
              <ChevronRight className="h-3 w-3 text-slate-500" />
            )}
            <Users className="h-3 w-3 text-slate-500" />
            <span className="text-[11px] text-slate-600 dark:text-slate-400 font-medium">{reports.length}</span>
          </div>
        )}
      </div>
    );
  };

  // Render tree structure with connecting lines
  const TreeLevel = ({ levelEmployees, isRoot = false }: { levelEmployees: any[]; isRoot?: boolean }) => {
    if (levelEmployees.length === 0) return null;

    return (
      <div className="flex flex-col items-center">
        <div className="flex flex-wrap justify-center gap-6">
          {levelEmployees.map((employee: any) => {
            const reports = getDirectReports(employee.id);
            const hasReports = reports.length > 0;
            const isExpanded = expandedManagerIds.has(employee.id);

            return (
              <div key={employee.id} className="flex flex-col items-center">
                <OrgCard employee={employee} isRoot={isRoot && levelEmployees.length === 1} />

                {hasReports && isExpanded && (
                  <>
                    <div className="w-0.5 h-8 bg-slate-300 dark:bg-slate-600" />

                    {reports.length > 1 && (
                      <div
                        className="h-0.5 bg-slate-300 dark:bg-slate-600"
                        style={{
                          width: `${Math.min(reports.length * 200, 800)}px`,
                          maxWidth: '90vw'
                        }}
                      />
                    )}

                    <div className="flex flex-wrap justify-center gap-4 mt-0">
                      {reports.map((report: any) => {
                        const subReports = getDirectReports(report.id);
                        const hasSubReports = subReports.length > 0;
                        const isSubExpanded = expandedManagerIds.has(report.id);

                        return (
                          <div key={report.id} className="flex flex-col items-center">
                            <div className="w-0.5 h-8 bg-slate-300 dark:bg-slate-600" />
                            <OrgCard employee={report} />

                            {hasSubReports && isSubExpanded && (
                              <>
                                <div className="w-0.5 h-6 bg-slate-300 dark:bg-slate-600" />

                                {subReports.length > 1 && (
                                  <div
                                    className="h-0.5 bg-slate-300 dark:bg-slate-600"
                                    style={{
                                      width: `${Math.min(subReports.length * 180, 600)}px`,
                                      maxWidth: '80vw'
                                    }}
                                  />
                                )}

                                <div className="flex flex-wrap justify-center gap-3 mt-0">
                                  {subReports.map((sub: any) => (
                                    <div key={sub.id} className="flex flex-col items-center">
                                      <div className="w-0.5 h-6 bg-slate-300 dark:bg-slate-600" />
                                      <OrgCard employee={sub} />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const assignedEmployees = employees.filter(e => e.managerEmployeeId);

  return (
    <div className="space-y-4">
      {topLevelManagers.length === 0 && assignedEmployees.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No organizational hierarchy configured</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Contact HR to set up reporting relationships</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-8">
          <div className="min-w-fit py-4 px-2">
            <TreeLevel levelEmployees={topLevelManagers} isRoot />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Company() {
  const { currentRole } = useRole();
  // Employees don't see celebratory tabs (birthdays/anniversaries); employers do.
  const isEmployee = currentRole === "employee";
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [directoryView, setDirectoryView] = useState<"grid" | "list">("grid");
  const [orgChartView, setOrgChartView] = useState<"department" | "tree">("department");
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [birthdayRange, setBirthdayRange] = useState<"this-month" | "30" | "60" | "90">("this-month");
  const [anniversaryRange, setAnniversaryRange] = useState<"this-month" | "30" | "60" | "90">("this-month");

  const { data: employees = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  const normalizedEmployees = useMemo(() => {
    return employees.map((e: any) => ({
      ...e,
      hireDate: e.hireDate ? new Date(e.hireDate) : null,
      dateOfBirth: e.dateOfBirth ? new Date(e.dateOfBirth) : null,
    }));
  }, [employees]);

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(normalizedEmployees);
  }, [normalizedEmployees]);

  const enrichedEmployees = useMemo(() => {
    const today = new Date();
    return sortedEmployees.map(e => {
      const yearsOfService = e.hireDate ? differenceInYears(today, e.hireDate) : 0;
      return {
        ...e,
        yearsOfService,
      };
    });
  }, [sortedEmployees]);

  const activeEmployees = enrichedEmployees.filter(e => e.status !== 'terminated');
  const totalEmployees = activeEmployees.length;

  const departmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    activeEmployees.forEach((emp) => {
      const dept = emp.department || 'Unassigned';
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return counts;
  }, [activeEmployees]);

  const filteredEmployees = useMemo(() => {
    return activeEmployees.filter(emp => {
      const matchesSearch = !searchTerm || 
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDepartment = departmentFilter === "all" || emp.department === departmentFilter;
      
      return matchesSearch && matchesDepartment;
    });
  }, [activeEmployees, searchTerm, departmentFilter]);

  const orgChart = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    activeEmployees.forEach(emp => {
      const dept = emp.department || 'Unassigned';
      if (!grouped[dept]) grouped[dept] = [];
      grouped[dept].push(emp);
    });
    Object.keys(grouped).forEach(dept => {
      grouped[dept] = sortEmployeesByFullName(grouped[dept]);
    });
    return grouped;
  }, [activeEmployees]);

  const today = startOfDay(new Date());
  const thisYear = today.getFullYear();

  const getFilterEndDate = (range: string) => {
    if (range === "this-month") return null;
    return addDays(today, parseInt(range));
  };

  const upcomingBirthdays = useMemo(() => {
    const endDate = getFilterEndDate(birthdayRange);
    return activeEmployees
      .filter(e => e.dateOfBirth)
      .filter(e => {
        const bday = new Date(thisYear, e.dateOfBirth.getMonth(), e.dateOfBirth.getDate());
        if (birthdayRange === "this-month") {
          return isSameMonth(bday, today);
        }
        return (isAfter(bday, today) || bday.getTime() === today.getTime()) && 
               endDate && isBefore(bday, endDate);
      })
      .sort((a, b) => {
        const aDate = new Date(thisYear, a.dateOfBirth.getMonth(), a.dateOfBirth.getDate());
        const bDate = new Date(thisYear, b.dateOfBirth.getMonth(), b.dateOfBirth.getDate());
        return aDate.getTime() - bDate.getTime();
      });
  }, [activeEmployees, birthdayRange, today, thisYear]);

  const upcomingAnniversaries = useMemo(() => {
    const endDate = getFilterEndDate(anniversaryRange);
    return activeEmployees
      .filter(e => e.hireDate)
      .filter(e => {
        const anniversary = new Date(thisYear, e.hireDate.getMonth(), e.hireDate.getDate());
        if (anniversaryRange === "this-month") {
          return isSameMonth(anniversary, today);
        }
        return (isAfter(anniversary, today) || anniversary.getTime() === today.getTime()) && 
               endDate && isBefore(anniversary, endDate);
      })
      .sort((a, b) => {
        const aDate = new Date(thisYear, a.hireDate.getMonth(), a.hireDate.getDate());
        const bDate = new Date(thisYear, b.hireDate.getMonth(), b.hireDate.getDate());
        return aDate.getTime() - bDate.getTime();
      });
  }, [activeEmployees, anniversaryRange, today, thisYear]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Company</h1>
        <p className="text-muted-foreground">View your coworkers and company structure</p>
      </div>

      <Tabs defaultValue="directory" className="space-y-6">
        <TabsList>
          <TabsTrigger value="directory" className="gap-2">
            <Users className="h-4 w-4" />
            Directory
          </TabsTrigger>
          <TabsTrigger value="orgchart" className="gap-2">
            <Building2 className="h-4 w-4" />
            Org Chart
          </TabsTrigger>
          {!isEmployee && (
            <>
              <TabsTrigger value="birthdays" className="gap-2">
                <Cake className="h-4 w-4" />
                Birthdays
              </TabsTrigger>
              <TabsTrigger value="anniversaries" className="gap-2">
                <Trophy className="h-4 w-4" />
                Anniversaries
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="directory" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Employee Directory
                  </CardTitle>
                  <CardDescription>{filteredEmployees.length} of {totalEmployees} coworkers</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={directoryView === "grid" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDirectoryView("grid")}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={directoryView === "list" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDirectoryView("list")}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, position, or department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {Object.keys(departmentCounts).filter(dept => dept && dept.trim() !== '').sort().map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {directoryView === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredEmployees.map((employee: any) => (
                    <div 
                      key={employee.id}
                      className="p-4 border rounded-lg hover:bg-muted/25 cursor-pointer transition-colors"
                      onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Avatar>
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback>
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {employee.firstName} {employee.lastName}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">
                            {employee.position}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{employee.department}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{employee.email}</span>
                        </div>
                        {(employee.city || employee.state) && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate">{employee.city}{employee.city && employee.state ? ', ' : ''}{employee.state}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Position</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredEmployees.map((employee: any) => (
                        <tr 
                          key={employee.id}
                          className="hover:bg-muted/25 cursor-pointer"
                          onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                                <AvatarFallback className="text-xs">
                                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm">
                                {employee.firstName} {employee.lastName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">{employee.position}</td>
                          <td className="px-4 py-3 text-sm">{employee.department}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{employee.email}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {employee.city}{employee.city && employee.state ? ', ' : ''}{employee.state || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredEmployees.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No coworkers found matching your criteria</p>
                </div>
              )}
              {isLoading && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Loading directory...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orgchart" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Organization Structure
                  </CardTitle>
                  <CardDescription>
                    {orgChartView === "department" 
                      ? "Employees organized by department"
                      : "Hierarchical view showing reporting relationships"}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={orgChartView === "department" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setOrgChartView("department")}
                  >
                    <Grid3X3 className="h-4 w-4 mr-2" />
                    Departments
                  </Button>
                  <Button
                    variant={orgChartView === "tree" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setOrgChartView("tree")}
                  >
                    <ChevronRight className="h-4 w-4 mr-2" />
                    Org Tree
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {orgChartView === "department" ? (
                <div className="space-y-6">
                  {Object.entries(orgChart)
                    .sort((a, b) => b[1].length - a[1].length)
                    .map(([department, deptEmployees]) => {
                      const managers = deptEmployees.filter(emp => 
                        enrichedEmployees.some(e => e.managerEmployeeId === emp.id)
                      );
                      
                      return (
                        <div key={department} className="border rounded-lg overflow-hidden">
                          <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                              <h3 className="font-semibold text-lg">{department}</h3>
                            </div>
                            <Badge variant="secondary">{deptEmployees.length} employees</Badge>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                              {deptEmployees.map((employee: any) => {
                                const isManager = managers.includes(employee);
                                const directReports = enrichedEmployees.filter(e => e.managerEmployeeId === employee.id).length;
                                
                                return (
                                  <div 
                                    key={employee.id} 
                                    className={cn(
                                      "flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/25 transition-colors cursor-pointer",
                                      isManager && "border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20"
                                    )}
                                    onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                                  >
                                    <Avatar className="h-10 w-10">
                                      {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                                      <AvatarFallback className={isManager ? "bg-blue-100 text-blue-700" : ""}>
                                        {employee.firstName?.[0]}{employee.lastName?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-sm truncate">
                                          {employee.firstName} {employee.lastName}
                                        </p>
                                        {isManager && (
                                          <Badge variant="outline" className="text-[10px] h-4 px-1 border-blue-300 text-blue-700">
                                            Lead
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground truncate">{employee.position}</p>
                                      {directReports > 0 && (
                                        <span className="text-[10px] text-muted-foreground flex items-center gap-1 mt-1">
                                          <Users className="h-3 w-3" /> {directReports} reports
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <OrgTreeView 
                  employees={enrichedEmployees} 
                  onEmployeeClick={(emp) => { setSelectedEmployee(emp); setIsEmployeeDialogOpen(true); }} 
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {!isEmployee && (
        <TabsContent value="birthdays" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cake className="h-5 w-5 text-pink-500" />
                    Upcoming Birthdays
                  </CardTitle>
                  <CardDescription>Celebrate your coworkers</CardDescription>
                </div>
                <Select value={birthdayRange} onValueChange={(v: any) => setBirthdayRange(v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="30">Next 30 Days</SelectItem>
                    <SelectItem value="60">Next 60 Days</SelectItem>
                    <SelectItem value="90">Next 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingBirthdays.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingBirthdays.map((employee: any) => {
                    const birthdayDate = new Date(thisYear, employee.dateOfBirth.getMonth(), employee.dateOfBirth.getDate());
                    const daysUntil = differenceInDays(birthdayDate >= today ? birthdayDate : new Date(thisYear + 1, employee.dateOfBirth.getMonth(), employee.dateOfBirth.getDate()), today);
                    
                    return (
                      <div 
                        key={employee.id} 
                        className={cn(
                          "flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/25",
                          daysUntil === 0 && "border-pink-300 bg-pink-50/50 dark:bg-pink-950/20 dark:border-pink-800"
                        )}
                        onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                      >
                        <Avatar className="h-12 w-12">
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback>
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-semibold">
                            {employee.firstName} {employee.lastName}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {employee.position} • {employee.department}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-sm text-pink-600 dark:text-pink-400">
                              <Cake className="h-3.5 w-3.5 inline mr-1" />
                              {format(employee.dateOfBirth, 'MMMM do')}
                            </p>
                            {daysUntil === 0 ? (
                              <Badge className="bg-pink-500">Today!</Badge>
                            ) : daysUntil > 0 && daysUntil <= 7 ? (
                              <Badge variant="outline" className="text-pink-600 border-pink-300">
                                In {daysUntil} days
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Cake className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No birthdays in this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {!isEmployee && (
        <TabsContent value="anniversaries" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Work Anniversaries
                  </CardTitle>
                  <CardDescription>Celebrate coworker milestones</CardDescription>
                </div>
                <Select value={anniversaryRange} onValueChange={(v: any) => setAnniversaryRange(v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="30">Next 30 Days</SelectItem>
                    <SelectItem value="60">Next 60 Days</SelectItem>
                    <SelectItem value="90">Next 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingAnniversaries.length > 0 ? (
                <div className="space-y-3">
                  {upcomingAnniversaries.map((employee: any) => {
                    const anniversaryDate = new Date(thisYear, employee.hireDate.getMonth(), employee.hireDate.getDate());
                    const daysUntil = differenceInDays(anniversaryDate >= today ? anniversaryDate : new Date(thisYear + 1, employee.hireDate.getMonth(), employee.hireDate.getDate()), today);
                    const yearsNext = employee.yearsOfService + 1;
                    
                    return (
                      <div 
                        key={employee.id} 
                        className={cn(
                          "flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/25",
                          daysUntil === 0 && "border-yellow-200 bg-yellow-50/50 dark:bg-yellow-950/20 dark:border-yellow-800"
                        )}
                        onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                      >
                        <Avatar className="h-14 w-14">
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback className="text-lg">
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">
                              {employee.firstName} {employee.lastName}
                            </h3>
                            <Badge variant="secondary">{yearsNext} years</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {employee.position} • {employee.department}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-sm text-blue-600 dark:text-blue-400">
                              <CalendarDays className="h-3.5 w-3.5 inline mr-1" />
                              {format(employee.hireDate, 'MMMM do')}
                            </p>
                            {daysUntil === 0 ? (
                              <Badge variant="default" className="bg-green-500">Today!</Badge>
                            ) : daysUntil > 0 && daysUntil <= 7 ? (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                In {daysUntil} days
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No work anniversaries in this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      <Dialog open={isEmployeeDialogOpen} onOpenChange={setIsEmployeeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Information</DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {selectedEmployee.profilePicture && <AvatarImage src={selectedEmployee.profilePicture} />}
                  <AvatarFallback className="text-xl">
                    {selectedEmployee.firstName?.[0]}{selectedEmployee.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </h3>
                  <p className="text-muted-foreground">{selectedEmployee.position}</p>
                  <Badge variant="outline" className="mt-1">{selectedEmployee.department}</Badge>
                </div>
              </div>
              
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${selectedEmployee.email}`} className="text-blue-600 hover:underline">
                    {selectedEmployee.email}
                  </a>
                </div>
                {selectedEmployee.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${selectedEmployee.phone}`} className="text-blue-600 hover:underline">
                      {selectedEmployee.phone}
                    </a>
                  </div>
                )}
                {(selectedEmployee.city || selectedEmployee.state) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedEmployee.city}{selectedEmployee.city && selectedEmployee.state ? ', ' : ''}{selectedEmployee.state}</span>
                  </div>
                )}
                {selectedEmployee.hireDate && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>Joined {format(selectedEmployee.hireDate, 'MMMM yyyy')} ({selectedEmployee.yearsOfService} years)</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
